import { createStore, type StoreApi } from "zustand";
import type { InputCaptchaElement } from "@/ui/input-captcha/input-captcha";
import { getCsrf } from "@/utils/csrf";
import { Network, NetworkError } from "@/utils/network";
import { sleep } from "@/utils/timer";
import type { InputV3 } from "../input/input-v3";

interface DynamicFormResponse {
  redirectUrl?: string;
}

export class DynamicFormElement extends HTMLElement {
  private form: HTMLFormElement | null = null;
  private state: StoreApi<{
    error: NetworkError | Error | undefined;
    isLoading: boolean;
    isValidating: boolean;
  }>;
  private error: HTMLElement | null = null;

  constructor() {
    super();

    this.state = createStore(() => ({
      error: undefined,
      isLoading: false,
      isValidating: false,
    }));
  }

  connectedCallback() {
    this.form = this.querySelector<HTMLFormElement>("form");
    this.error = this.querySelector("dynamic-form-error");

    const form = this.form;

    if (form === null) {
      return;
    }

    const submitBtns = form.querySelectorAll(`.btn[type="submit"]`);
    const captcha = form.querySelector<InputCaptchaElement>("input-captcha");

    form.addEventListener("submit", async (event) => {
      event.preventDefault();

      const { isValidating, isLoading } = this.state.getState();

      if (isValidating === true || isLoading === true) {
        return;
      }

      this.state.setState({
        isLoading: true,
        error: undefined,
      });

      const formData = new FormData(form);

      formData.append("_csrf", getCsrf());

      await sleep(1000);

      try {
        const { data } = await Network.post<DynamicFormResponse>(
          form.action,
          formData,
        );

        if (data.redirectUrl !== undefined) {
          window.location.assign(data.redirectUrl);
        }

        this.dispatchEvent(new CustomEvent("dynamic-form:onSuccess"));
      } catch (error) {
        if (error instanceof Error) {
          this.state.setState({
            error,
          });
        } else {
          throw error;
        }
      } finally {
        this.state.setState({
          isLoading: false,
        });

        captcha?.reset();
      }
    });

    captcha?.addEventListener(
      "captcha:onValidating",
      ({ detail: isValidating }) => {
        this.state.setState({
          isValidating,
        });
      },
    );

    this.state.subscribe((state, prevState) => {
      this.classList.toggle("loading", state.isLoading);

      submitBtns.forEach((btn) => {
        btn.classList.toggle("btn-loading", state.isValidating);
      });

      if (state.error !== prevState.error) {
        if (state.error === undefined) {
          setInputErrors(form, undefined);
          setCaptchaErrors(form, undefined);
          this.error?.replaceChildren();
        } else {
          if (state.error instanceof NetworkError) {
            setInputErrors(form, state.error);
            setCaptchaErrors(form, state.error);

            if (state.error.errors.size === 0) {
              this.error?.replaceChildren(new Text(state.error.message));
            }
          } else {
            this.error?.replaceChildren(new Text(state.error.message));
          }

          this.dispatchEvent(
            new CustomEvent("dynamic-form:onError", { detail: state.error }),
          );
        }
      }
    });

    this.state.setState({
      isValidating: captcha?.getState().isValidating ?? false,
    });
  }

  setError(error: NetworkError | Error | undefined) {
    this.state.setState({
      error,
    });
  }
}

const setInputErrors = (form: HTMLFormElement, error?: NetworkError) => {
  const inputs = form.querySelectorAll<InputV3>("input-v3");

  inputs.forEach((input) => {
    input.setError(error?.errors?.get(input.getName()));
  });
};

const setCaptchaErrors = (form: HTMLFormElement, error?: NetworkError) => {
  const captchas = form.querySelectorAll<InputCaptchaElement>("input-captcha");

  captchas?.forEach((captcha) => {
    captcha.setError(error?.errors?.get(captcha.getName()));
  });
};
